import { Fragment } from 'react';
import { Document, Text, Page, StyleSheet, Image, View, Font } from '@react-pdf/renderer';
import MIMWheel from "../../assets/images/MIMWheel.png";
import LIMWheel from "../../assets/images/LIMWheel.png";
import LogoWhite from "../../assets/images/K2GLogoWhite.png";
import TopLogo from "../../assets/images/logo2.png";
import FullLogo from "../../assets/images/fullLogo.png";

Font.register({
    family: "Tahoma",
    src: "/fonts/Tahoma-Regular.ttf",
    fonts: [
        { src: "/fonts/Tahoma-Regular.ttf", fontWeight: "normal" },
        { src: "/fonts/TAHOMAB0.TTF", fontWeight: "bold" },
    ]
});

/* ── Brand Constants ── */
const C = {
    BLUE: "#0F5385",
    GREEN: "#69AF45",
    LIGHT_BLUE: "#2B8DCC",
    ORANGE: "#e67e22",
    RED: "#E74C3C",
    DARK: "#2D3748",
    MID: "#4A5568",
    LIGHT: "#718096",
    MUTED: "#A0AEC0",
    BG: "#F7FAFC",
    DIVIDER: "#E2E8F0",
    WHITE: "#FFFFFF",
};

/* ── Helpers ── */
const mapValueToLabel = (value) => {
    if (!value || value === 0) return "";
    if (value <= 37) return "Not Confident";
    if (value <= 62) return "Somewhat Confident";
    if (value <= 87) return "Confident";
    return "Very Confident";
};

const getConfidenceColor = (value) => {
    if (!value || value === 0) return "#CBD5E0";
    if (value <= 37) return C.RED;
    if (value <= 62) return C.ORANGE;
    if (value <= 87) return C.LIGHT_BLUE;
    return C.GREEN;
};

const DEFAULT_THRESHOLDS = {
    strength: { label: "STRENGTH", minScore: 90 },
    developing: { label: "DEVELOPING", minScore: 60 },
    opportunity: { label: "OPPORTUNITY" },
};

const resolveThresholds = (report) => {
    const t = report?.ratingThresholds;
    return {
        strength: {
            label: t?.strength?.label || DEFAULT_THRESHOLDS.strength.label,
            minScore: typeof t?.strength?.minScore === "number" ? t.strength.minScore : DEFAULT_THRESHOLDS.strength.minScore,
        },
        developing: {
            label: t?.developing?.label || DEFAULT_THRESHOLDS.developing.label,
            minScore: typeof t?.developing?.minScore === "number" ? t.developing.minScore : DEFAULT_THRESHOLDS.developing.minScore,
        },
        opportunity: {
            label: t?.opportunity?.label || DEFAULT_THRESHOLDS.opportunity.label,
        },
    };
};

const makeRatingFns = (thresholds) => {
    const getSectionBand = (value) => {
        if (!value || value === 0) return { label: "", color: "#CBD5E0" };
        if (value < thresholds.developing.minScore) return { label: thresholds.opportunity.label, color: C.ORANGE };
        if (value < thresholds.strength.minScore) return { label: thresholds.developing.label, color: C.LIGHT_BLUE };
        return { label: thresholds.strength.label, color: C.GREEN };
    };
    const getRating = (pct) => {
        if (pct >= thresholds.strength.minScore) return thresholds.strength.label;
        if (pct >= thresholds.developing.minScore) return thresholds.developing.label;
        return thresholds.opportunity.label;
    };
    const getRatingColor = (rating) => {
        if (rating === thresholds.strength.label) return C.GREEN;
        if (rating === thresholds.developing.label) return C.LIGHT_BLUE;
        return C.ORANGE;
    };
    return { getSectionBand, getRating, getRatingColor };
};

/* ── Inline ® handler ── */
const inlineReg = (text, style) => {
    if (!text) return null;
    const cleaned = text.replace(/<sup>&reg;<\/sup>/g, '®').replace(/<sup>®<\/sup>/g, '®');
    if (!cleaned.includes('®')) return <Text style={style}>{cleaned}</Text>;
    const parts = cleaned.split('®');
    return (
        <Text style={style}>
            {parts.map((part, i) => (
                i < parts.length - 1
                    ? <Text key={i}>{part}<Text style={{ fontSize: 7, verticalAlign: 'super' }}>®</Text></Text>
                    : <Text key={i}>{part}</Text>
            ))}
        </Text>
    );
};

const regTitle = (text, size = 20, color = C.WHITE) => {
    if (!text) return null;
    if (!text.includes("<sup>&reg;</sup>")) return <Text style={{ fontSize: size, fontWeight: "bold", color }}>{text}</Text>;
    const [before, after] = text.split("<sup>&reg;</sup>");
    const supSize = Math.round(size * 0.5);
    return (
        <Text style={{ fontSize: size, fontWeight: "bold", color }}>
            {before}<Text style={{ fontSize: supSize, verticalAlign: 'super', color }}>®</Text>{after ? ` ${after}` : ""}
        </Text>
    );
};

/* ── Reusable Components ── */

/* Footer elements must be inlined directly as Page children for `fixed` to work */

const SectionBanner = ({ title, color = C.BLUE }) => (
    <View style={{ backgroundColor: color, paddingVertical: 18, paddingHorizontal: 24, marginLeft: -55, marginRight: -55, marginTop: -50, marginBottom: 20 }}>
        {regTitle(title, 18, C.WHITE)}
    </View>
);

const GreenAccent = ({ width = 50 }) => (
    <View style={{ width, height: 3, backgroundColor: C.GREEN, marginTop: 10, marginBottom: 16 }} />
);

const Divider = () => (
    <View style={{ height: 1, backgroundColor: C.DIVIDER, marginVertical: 16 }} />
);

/* ── Bar Chart: Single Question ── */
const BAR_LABELS = ["Self", "Manager", "Direct Reports", "Peers", "Other Colleagues"];
const BAR_HEIGHT = 16;
const LABEL_WIDTH = 85;

const NativeChart360 = ({ question, values, isLast, color }) => (
    <View style={{ marginBottom: isLast ? 0 : 8, marginTop: 6 }} wrap={false}>
        <Text style={{ fontSize: 10.5, fontWeight: "bold", color: color || C.BLUE, marginBottom: 8 }}>{question}</Text>
        {BAR_LABELS.map((label, i) => {
            const val = values[i] || 0;
            const pct = Math.max((val / 100) * 100, 0);
            const confidenceLabel = mapValueToLabel(val);
            const barColor = getConfidenceColor(val);
            return (
                <View key={i} style={{ flexDirection: "row", alignItems: "center", marginBottom: 3 }}>
                    <Text style={{ width: LABEL_WIDTH, fontSize: 8, fontWeight: "bold", color: C.MID, textAlign: "right", paddingRight: 8 }}>{label}</Text>
                    <View style={{ flex: 1, height: BAR_HEIGHT, backgroundColor: "#EDF2F7", position: "relative" }}>
                        <View style={{ position: "absolute", left: "25%", top: 0, bottom: 0, width: 0.5, backgroundColor: "#D8DBE0" }} />
                        <View style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 0.5, backgroundColor: "#D8DBE0" }} />
                        <View style={{ position: "absolute", left: "75%", top: 0, bottom: 0, width: 0.5, backgroundColor: "#D8DBE0" }} />
                        {pct > 0 && <View style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct}%`, backgroundColor: barColor }} />}
                        {confidenceLabel !== "" && (
                            <Text style={{ position: "absolute", left: 4, top: 0, bottom: 0, fontSize: 7, fontWeight: "bold", color: C.WHITE, lineHeight: BAR_HEIGHT * 0.95 }}>{confidenceLabel}</Text>
                        )}
                    </View>
                </View>
            );
        })}
        <View style={{ flexDirection: "row", marginLeft: LABEL_WIDTH, marginTop: 2 }}>
            <Text style={{ flex: 1, fontSize: 6.5, color: C.MUTED, textAlign: "left" }}>Not Confident</Text>
            <Text style={{ flex: 1, fontSize: 6.5, color: C.MUTED, textAlign: "center" }}>Somewhat Confident</Text>
            <Text style={{ flex: 1, fontSize: 6.5, color: C.MUTED, textAlign: "center" }}>Confident</Text>
            <Text style={{ flex: 1, fontSize: 6.5, color: C.MUTED, textAlign: "right" }}>Very Confident</Text>
        </View>
        {!isLast && <View style={{ height: 0.5, backgroundColor: C.DIVIDER, marginTop: 10 }} />}
    </View>
);

/* ── Bar Chart: Sections Summary ── */
const NativeSectionsChart = ({ sections, getSectionBand }) => {
    if (!sections || sections.length === 0) return null;
    return (
        <View style={{ marginTop: 8, marginBottom: 8 }}>
            {sections.map((s, i) => {
                const pct = Math.min(Math.max(s.positive || 0, 0), 100);
                const band = getSectionBand(pct);
                return (
                    <View key={i} style={{ flexDirection: "row", alignItems: "center", marginBottom: 8 }}>
                        <Text style={{ width: 170, fontSize: 9, fontWeight: "bold", color: C.DARK, textAlign: "right", paddingRight: 10 }}>{s.name}</Text>
                        <View style={{ flex: 1, height: 22, backgroundColor: "#EDF2F7", position: "relative" }}>
                            {pct > 0 && <View style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct}%`, backgroundColor: band.color }} />}
                            {band.label !== "" && (
                                <Text style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct}%`, fontSize: 8.5, fontWeight: "bold", color: C.WHITE, textAlign: "center", lineHeight: 20 }}>{band.label}</Text>
                            )}
                        </View>
                        <Text style={{ width: 40, fontSize: 8, fontWeight: "bold", color: C.DARK, textAlign: "left", paddingLeft: 6 }}>{pct.toFixed(0)}%</Text>
                    </View>
                );
            })}
            <View style={{ flexDirection: "row", marginLeft: 170, marginRight: 40, marginTop: 2 }}>
                {[0, 20, 40, 60, 80, 100].map((v) => (
                    <Text key={v} style={{ flex: 1, fontSize: 6.5, color: C.MUTED, textAlign: v === 0 ? "left" : v === 100 ? "right" : "center" }}>{v}%</Text>
                ))}
            </View>
        </View>
    );
};

/* ── Rating Table ── */
const RatingTable = ({ thresholds }) => {
    const t = thresholds || DEFAULT_THRESHOLDS;
    const developingMin = t.developing.minScore;
    const strengthMin = t.strength.minScore;
    const developingMax = strengthMin - 1;
    const rows = [
        { score: `${strengthMin}% or higher`, label: t.strength.label, color: C.GREEN, bg: "#F0FFF4" },
        { score: `${developingMin}%–${developingMax}%`, label: t.developing.label, color: C.LIGHT_BLUE, bg: "#EBF8FF" },
        { score: `Less than ${developingMin}%`, label: t.opportunity.label, color: C.ORANGE, bg: "#FFFAF0" },
    ];
    return (
    <View style={{ width: "75%", margin: "16 auto", overflow: "hidden" }}>
        <View style={{ flexDirection: "row", backgroundColor: C.BLUE, paddingVertical: 10, paddingHorizontal: 16 }}>
            <View style={{ width: "50%" }}>
                <Text style={{ fontSize: 10, fontWeight: "bold", color: C.WHITE }}>Score</Text>
                <Text style={{ fontSize: 7.5, color: "#B8CCE0", marginTop: 2 }}>(% of positive responses)</Text>
            </View>
            <View style={{ width: "50%" }}>
                <Text style={{ fontSize: 10, fontWeight: "bold", color: C.WHITE }}>Rating</Text>
            </View>
        </View>
        {rows.map((row, i) => (
            <View key={i} style={{ flexDirection: "row", backgroundColor: row.bg, paddingVertical: 10, paddingHorizontal: 16, borderBottomWidth: i < 2 ? 1 : 0, borderBottomColor: C.DIVIDER, borderLeftWidth: 4, borderLeftColor: row.color }}>
                <View style={{ width: "50%" }}>
                    <Text style={{ fontSize: 10, color: C.DARK }}>{row.score}</Text>
                </View>
                <View style={{ width: "50%" }}>
                    <Text style={{ fontSize: 10, fontWeight: "bold", color: row.color }}>{row.label}</Text>
                </View>
            </View>
        ))}
    </View>
    );
};

/* ── How to Use List ── */
const BulletList = ({ items, title }) => (
    <View style={{ marginTop: 20 }}>
        <Text style={s.h2}>{title}</Text>
        {items.map((item, index) => (
            <View key={index} style={{ flexDirection: 'row', marginBottom: 8, alignItems: 'flex-start' }}>
                <View style={{ width: 6, height: 6, backgroundColor: C.GREEN, marginTop: 4, marginRight: 10 }} />
                {inlineReg(item, { flex: 1, fontSize: 10, lineHeight: 1.5, color: C.DARK })}
            </View>
        ))}
    </View>
);

/* ── Styles ── */
const s = StyleSheet.create({
    page: {
        paddingTop: 50,
        paddingBottom: 55,
        paddingHorizontal: 55,
        fontFamily: "Tahoma",
    },
    coverPage: {
        padding: 0,
        fontFamily: "Tahoma",
    },
    h1: { fontSize: 20, fontWeight: "bold", color: C.BLUE, marginBottom: 8 },
    h2: { fontSize: 14, fontWeight: "bold", color: C.BLUE, marginTop: 16, marginBottom: 8 },
    h3: { fontSize: 12, fontWeight: "bold", color: C.BLUE, marginTop: 12, marginBottom: 6 },
    body: { fontSize: 10, lineHeight: 1.5, color: C.DARK, marginBottom: 8 },
    bodySmall: { fontSize: 9, lineHeight: 1.5, color: C.MID },
    caption: { fontSize: 7.5, color: C.MUTED },
});

/* ── Main Component ── */
const Assessment360PDF = ({ chartData360, blocks, name, slug, report, info, sectionsData360, dateCompleted, extraQ, summaryScores360 = [], responseRate, blockReflections = {} }) => {

    const programName = slug === "mim_360_assessment" ? "Managers in Motion<sup>&reg;</sup>" : "Leaders in Motion<sup>&reg;</sup>";
    const wheelImage = slug === "mim_360_assessment" ? MIMWheel : LIMWheel;
    const dimCount = slug === "lim_360_assessment" ? "six" : "four";

    const thresholds = resolveThresholds(report);
    const { getSectionBand, getRating, getRatingColor } = makeRatingFns(thresholds);

    const coverSubtitle = report?.coverSubtitle || "360-Degree Assessment Feedback Report";
    const summaryBannerTitle = report?.summaryBannerTitle || `${slug === "lim_360_assessment" ? "Leaders in Motion<sup>&reg;</sup>" : "Managers in Motion<sup>&reg;</sup>"} 360-Degree Summary`;
    const summaryMethodology = report?.summaryMethodology || `Your report reflects your confidence level on ${dimCount} dimensions and compares that to the perception of others. Each dimension had 10 related questions. To calculate your percentage score in each dimension, we counted the number of responses that were "Very Confident" or "Confident" and used these two answers to represent "Positive Responses". We then calculated the percent of your total answers that were "Positive Responses".`;
    const summaryCategorization = report?.summaryCategorization || "For developmental purposes, we categorized your score into three different levels to help you focus your development priorities.";
    const summaryFollowingPages = report?.summaryFollowingPages || "In the pages that follow, you can review your scores, question by question, to help you plan your development priorities.";
    const blockSummaryIntro = report?.blockSummaryIntro || `Here is a summary of your scores on each of the ${dimCount} blocks.`;
    const responseRateIntroTpl = report?.responseRateIntro || "You invited {invited} people to complete the survey. {responded} people responded.";
    const responseRateIntro = responseRateIntroTpl
        .replace("{invited}", String(responseRate?.totalInvited ?? 0))
        .replace("{responded}", String(responseRate?.totalResponded ?? 0));

    const textWithReg = (text, style = s.body) => {
        if (!text) return null;
        const cleaned = text.replace(/<sup>&reg;<\/sup>/g, '®').replace(/<sup>®<\/sup>/g, '®');
        if (!cleaned.includes('®')) return <Text style={style}>{cleaned}</Text>;
        const parts = cleaned.split('®');
        return (
            <Text style={style}>
                {parts.map((part, i) => (
                    i < parts.length - 1
                        ? <Text key={i}>{part}<Text style={{ fontSize: 7, verticalAlign: 'super' }}>®</Text></Text>
                        : <Text key={i}>{part}</Text>
                ))}
            </Text>
        );
    };

    const findInfoText = (blockId) => {
        const i = info.find((b) => b.block === blockId);
        return i?.text || "";
    };

    return (
        <Document>
            {/* ═══ COVER PAGE ═══ */}
            <Page style={s.coverPage}>
                <View style={{ backgroundColor: C.BLUE, paddingHorizontal: 55, paddingTop: 50, paddingBottom: 40 }}>
                    <Image src={LogoWhite} style={{ width: 120, marginBottom: 30 }} />
                    {regTitle(programName, 32, C.WHITE)}
                    <Text style={{ fontSize: 16, color: "#D6E4F0", marginTop: 6 }}>{coverSubtitle}</Text>
                    <View style={{ width: 50, height: 3, backgroundColor: C.GREEN, marginTop: 16, marginBottom: 16 }} />
                    <Text style={{ fontSize: 18, color: C.WHITE, marginTop: 4 }}>{name}</Text>
                    <Text style={{ fontSize: 12, color: "#B8CCE0", marginTop: 6 }}>{dateCompleted}</Text>
                    {responseRate && (
                        <Text style={{ fontSize: 11, color: "#B8CCE0", marginTop: 4 }}>
                            {responseRate.totalResponded} of {responseRate.totalInvited} reviewer{responseRate.totalInvited === 1 ? "" : "s"} responded
                        </Text>
                    )}
                </View>
                <View style={{ flex: 1, alignItems: "center", justifyContent: "center", paddingVertical: 30 }}>
                    <Image src={wheelImage} style={{ width: 480, objectFit: "contain" }} />
                </View>
                <Text style={{ position: 'absolute', bottom: 16, right: 55, fontSize: 8, color: C.MUTED }} render={({ pageNumber }) => `${pageNumber}`} fixed />
            </Page>

            {/* ═══ WELCOME / INTRO PAGE ═══ */}
            <Page style={s.page}>
                <View style={{ height: 4, backgroundColor: C.GREEN, marginBottom: 24, marginHorizontal: -55, }} />
                {regTitle(programName, 22, C.BLUE)}
                <Text style={{ fontSize: 14, color: C.GREEN, marginTop: 4 }}>{coverSubtitle}</Text>
                <Divider />
                {textWithReg(report?.welcome)}
                {report?.howToUse?.length > 0 && <BulletList items={report.howToUse} title="How to Use This Feedback" />}
                <View style={{ position: 'absolute', bottom: 40, left: 55, right: 55, height: 0.5, backgroundColor: C.DIVIDER }} fixed />
                <Image src={TopLogo} style={{ position: 'absolute', bottom: 12, left: 55, height: 22 }} fixed />
                <Text style={{ position: 'absolute', bottom: 16, right: 55, fontSize: 8, color: C.MUTED }} render={({ pageNumber }) => `${pageNumber}`} fixed />
            </Page>

            {/* ═══ SUMMARY PAGE ═══ */}
            <Page style={s.page}>
                <SectionBanner title={summaryBannerTitle} />
                <Text style={s.body}>{summaryMethodology}</Text>
                <Text style={s.body}>{summaryCategorization}</Text>
                <RatingTable thresholds={thresholds} />
                {responseRate && (
                    <>
                        <Text style={s.h2}>Response Rate ({responseRate.totalInvited > 0 ? Math.round((responseRate.totalResponded / responseRate.totalInvited) * 100) : 0}%)</Text>
                        <Text style={s.body}>{responseRateIntro}</Text>
                        <Text style={s.body}>{summaryFollowingPages}</Text>
                    </>
                )}
                {sectionsData360 && sectionsData360.length > 0 && (
                    <>
                        <Text style={s.h2}>Block Summary Scores</Text>
                        <Text style={s.body}>{blockSummaryIntro}</Text>
                        <NativeSectionsChart sections={sectionsData360} getSectionBand={getSectionBand} />
                    </>
                )}
                <View style={{ position: 'absolute', bottom: 40, left: 55, right: 55, height: 0.5, backgroundColor: C.DIVIDER }} fixed />
                <Image src={TopLogo} style={{ position: 'absolute', bottom: 12, left: 55, height: 22 }} fixed />
                <Text style={{ position: 'absolute', bottom: 16, right: 55, fontSize: 8, color: C.MUTED }} render={({ pageNumber }) => `${pageNumber}`} fixed />
            </Page>

            {/* ═══ BLOCK DETAIL PAGES ═══ */}
            {blocks.map((block, i) => {
                const scoreData = summaryScores360.find(sc => String(sc.blockId) === String(block._id));
                const rating = scoreData ? getRating(scoreData.positive) : "";
                const ratingColor = getRatingColor(rating);
                const questions = chartData360[block._id] || [];
                return (
                    <Fragment key={i}>
                        <Page style={s.page}>
                            <SectionBanner title={block.name} color={block.color || C.BLUE} />
                            {inlineReg(findInfoText(block._id), [s.body, { marginBottom: 4 }])}
                            <Text style={[s.bodySmall, { marginBottom: 12 }]}>Review the responses on all 10 questions. Then identify your priority areas of focus. Note: If a line is blank that means fewer than 3 people were able to respond to your question.</Text>
                            {questions.map((q, qIndex) => (
                                <NativeChart360
                                    key={qIndex}
                                    question={`${qIndex + 1}. ${q.question}`}
                                    values={[q.owner, q.manager, q.direct, q.peer, q.otherColleague]}
                                    color={block.color || C.BLUE}
                                    isLast={qIndex === questions.length - 1}
                                />
                            ))}
                            <View style={{ position: 'absolute', bottom: 40, left: 55, right: 55, height: 0.5, backgroundColor: C.DIVIDER }} fixed />
                <Image src={TopLogo} style={{ position: 'absolute', bottom: 12, left: 55, height: 22 }} fixed />
                <Text style={{ position: 'absolute', bottom: 16, right: 55, fontSize: 8, color: C.MUTED }} render={({ pageNumber }) => `${pageNumber}`} fixed />
                        </Page>
                        {scoreData && (
                            <Page style={s.page}>
                                <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 20 }}>
                                    <View style={{ width: 12, height: 12, backgroundColor: block.color || C.BLUE, marginRight: 10 }} />
                                    <Text style={{ fontSize: 18, fontWeight: "bold", color: C.BLUE }}>Category Score + Reflection</Text>
                                </View>
                                <View style={{ backgroundColor: C.BG, padding: 20, borderLeftWidth: 4, borderLeftColor: ratingColor, marginBottom: 16 }}>
                                    <Text style={{ fontSize: 14, fontWeight: "bold", color: C.BLUE }}>{block.name}</Text>
                                    <Text style={{ fontSize: 22, fontWeight: "bold", color: ratingColor, marginTop: 6 }}>{rating}</Text>
                                    {scoreData.positive !== undefined && (
                                        <Text style={{ fontSize: 10, color: C.MID, marginTop: 4 }}>Score: {scoreData.positive.toFixed(0)}%</Text>
                                    )}
                                </View>
                                <Text style={s.body}>This category "{block.name}" has been identified {rating} for you. As you reviewed the scores on each question what stands out as a priority focus area in this dimension? Where you might want to focus?</Text>
                                {blockReflections[block._id] ? (
                                    <Text style={[s.body, { marginTop: 12, backgroundColor: C.BG, padding: 12 }]}>{blockReflections[block._id]}</Text>
                                ) : (
                                    [1, 2, 3, 4, 5].map((_, li) => (
                                        <View key={li} style={{ height: 0.5, backgroundColor: C.DIVIDER, marginTop: 28 }} />
                                    ))
                                )}
                                <View style={{ position: 'absolute', bottom: 40, left: 55, right: 55, height: 0.5, backgroundColor: C.DIVIDER }} fixed />
                <Image src={TopLogo} style={{ position: 'absolute', bottom: 12, left: 55, height: 22 }} fixed />
                <Text style={{ position: 'absolute', bottom: 16, right: 55, fontSize: 8, color: C.MUTED }} render={({ pageNumber }) => `${pageNumber}`} fixed />
                            </Page>
                        )}
                    </Fragment>
                );
            })}

            {/* ═══ FOCUS AREAS PAGE ═══ */}
            <Page style={s.page}>
                <View style={{ alignItems: "center", marginBottom: 20 }}>
                    <Image src={FullLogo} style={{ width: "40%" }} />
                </View>
                <Text style={s.h1}>Focus Areas for Development</Text>
                <GreenAccent width={50} />
                {summaryScores360.length > 0 ? (
                    <>
                        <Text style={[s.body, { fontWeight: "bold" }]}>Your Summary Scores:</Text>
                        <NativeSectionsChart sections={summaryScores360} />
                    </>
                ) : (
                    <>
                        <Text style={s.body}>1. Review your assessment results.</Text>
                        <Text style={s.body}>2. Examine the scores for each dimension and question.</Text>
                        <Text style={s.body}>3. Use the guidance below to identify next steps for your development.</Text>
                        <Text style={s.body}>4. Set personal Action Commitments.</Text>
                    </>
                )}
                {[
                    { label: "Strength", desc: "(Scores of 90%+)", color: C.GREEN, text: "Dedicate yourself to a deeper understanding of the models and concepts in this area. Strive to become a true expert in all facets to drive consistent performance and improvement. What Action Commitments can you make to reinforce your strengths?" },
                    { label: "Developing", desc: "(Scores of 60%–89%)", color: C.LIGHT_BLUE, text: "You have opportunities for growth in these areas of competency. What Action Commitments can you make to build skills in these areas? Further education and regular practice will help solidify these skills and develop expertise." },
                    { label: "Opportunity", desc: "(Less than 60%)", color: C.ORANGE, text: "These areas present the biggest growth opportunities. Focus on understanding, reviewing, and applying the concepts. What Action Commitments can you make to mitigate the risks or consequences of these areas of development? Consider participating in individual workshops, online learning, or programs to enhance your skills." },
                ].map((item, idx) => (
                    <View key={idx} style={{ marginTop: 14 }}>
                        <View style={{ flexDirection: "row", alignItems: "center" }}>
                            <View style={{ width: 8, height: 8, backgroundColor: item.color, marginRight: 8 }} />
                            <Text style={{ fontSize: 12, fontWeight: "bold", color: C.BLUE }}>{item.label} {item.desc}</Text>
                        </View>
                        <Text style={[s.body, { marginTop: 4, marginLeft: 16 }]}>{item.text}</Text>
                    </View>
                ))}
                <View style={{ backgroundColor: C.BG, padding: 16, borderLeftWidth: 3, borderLeftColor: C.GREEN, marginTop: 20 }}>
                    <Text style={{ fontSize: 11, fontWeight: "bold", color: C.BLUE, marginBottom: 8 }}>Based on your feedback in all areas, identify the top priority 2 or 3 areas you will focus on for development:</Text>
                    <Text style={s.body}>{extraQ}</Text>
                </View>
                <View style={{ position: 'absolute', bottom: 40, left: 55, right: 55, height: 0.5, backgroundColor: C.DIVIDER }} fixed />
                <Image src={TopLogo} style={{ position: 'absolute', bottom: 12, left: 55, height: 22 }} fixed />
                <Text style={{ position: 'absolute', bottom: 16, right: 55, fontSize: 8, color: C.MUTED }} render={({ pageNumber }) => `${pageNumber}`} fixed />
            </Page>
        </Document>
    );
};

export default Assessment360PDF;
